import type React from 'react';
import DescendingOrderReport from './reports/DescendingOrderReport';
import PoetryProgramsReport from './reports/PoetryProgramsReport';
import AnthologyProgramsReport from './reports/AnthologyProgramsReport';
import SeedSourceReport from './reports/SeedSourceReport';
import ChristianProgramsReport from './reports/ChristianProgramsReport';

export type ReportId =
  | 'descending-order'
  | 'seed-source-programs'
  | 'poetry-programs'
  | 'anthology-programs'
  | 'christian-programs';

export interface ReportComponentProps {
  onBack: () => void;
}

export interface ReportDefinition {
  id: ReportId;
  name: string;
  headline: string;
  description: string;
  primaryActionLabel: string;
  component: React.ComponentType<ReportComponentProps>;
  tags?: string[];
}

export const reportDefinitions: ReportDefinition[] = [
  {
    id: 'descending-order',
    name: 'List of Programs',
    headline: '(in descending order)',
    description:
      'Live table of programs sorted by category count with BACI codes, organizations, prize tiers, total win opportunities, and vetting status.',
    primaryActionLabel: 'Open report',
    component: DescendingOrderReport,
    tags: ['priority', 'categories', 'prize-tiers']
  },
  {
    id: 'seed-source-programs',
    name: 'Priority Programs',
    headline: 'Alphabetical Index of the 50+ programs that have priority to fill, clean and categorize for BAM usage.',
    description:
      'Audit search readiness, category coverage, and launch program cards directly for the active seed-source priority cohort.',
    primaryActionLabel: 'Open seed list',
    component: SeedSourceReport,
    tags: ['priority', 'seed-sources', 'program-cards']
  },
  {
    id: 'poetry-programs',
    name: 'Poetry Programs',
    headline: 'Programs featuring poetry categories',
    description:
      'Spotlight on programs that currently include poetry-focused categories, including website links and vetting coverage.',
    primaryActionLabel: 'View poetry report',
    component: PoetryProgramsReport,
    tags: ['genre', 'poetry']
  },
  {
    id: 'anthology-programs',
    name: 'Anthology Programs',
    headline: 'Programs offering anthology opportunities',
    description:
      'Anthology-friendly programs with quick access to BACI codes, submission URLs, and prize opportunity stats.',
    primaryActionLabel: 'Open anthology report',
    component: AnthologyProgramsReport,
    tags: ['anthology', 'submission']
  },
  {
    id: 'christian-programs',
    name: 'Christian Awards Programs',
    headline: 'Programs aligned to Christian authors and categories',
    description:
      'Discover programs and categories referencing Christian themes, faith-based authorship, or gospel literature—keywords are editable in the report.',
    primaryActionLabel: 'View Christian report',
    component: ChristianProgramsReport,
    tags: ['genre', 'christian']
  }
];

export const getReportDefinition = (id: ReportId | null | undefined): ReportDefinition | undefined => {
  if (!id) return undefined;
  return reportDefinitions.find((report) => report.id === id);
};
